import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const JobHeader = ({
  job,
  isSharedView,
  startTimeEntry,
  completedTimeEntry,
  completionStatusEntry,
  isInProgress,
  isCompleted,
  isReadyForInspection,
  isInspectionJob,
  isInspectionComplete,
  isChecklistSubmitted,
  handleStartClean,
  handleCompleteClean,
  handleMarkInspectionComplete,
  handleOpenChecklistModal,
  handleShare,
  formatDateTime,
  getStatusText,
  getStatusColorClass
}) => {
  const navigate = useNavigate();

  const isCustomJob = () => {
    const standardCategories = ['CLEANING', 'INSPECTION', 'WALKTHROUGH'];
    return !standardCategories.includes(job?.category);
  };

  const completionEntry = completionStatusEntry || completedTimeEntry;

  return (
    <>
      {/* Mobile header */}
      <div className="lg:hidden px-2 lg:px-4 mt-3">
        <div className="bg-white shadow rounded-lg">
          <div className="px-2 lg:px-4 py-6 space-y-4">
            <h1 className="text-xl font-semibold text-gray-900">{job.property.name}</h1>
            {startTimeEntry && !completionEntry && (
              <div className="mt-2 inline-flex items-center px-3 py-1.5 mr-4 rounded-full text-sm bg-blue-100 text-blue-800">
                <svg className="h-5 w-5 mr-1.5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Job Start: {formatDateTime(startTimeEntry.timestamp)}
                {startTimeEntry.updatedBy && <span> by {startTimeEntry.updatedBy.name}</span>}
              </div>
            )}
            {completionEntry && (
              <div className="space-y-2 mt-2">
                {startTimeEntry && (
                  <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800">
                    <svg className="h-5 w-5 mr-1.5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    Job Start: {formatDateTime(startTimeEntry.timestamp)}
                  </div>
                )}
                <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-800">
                  <svg className="h-5 w-5 mr-1.5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  {completionStatusEntry?.status === 'READY_FOR_INSPECTION'
                    ? `Ready for inspection at ${formatDateTime(completionStatusEntry.timestamp)}`
                    : `Job completed at ${formatDateTime(completionEntry.timestamp)}`}
                </div>
              </div>
            )}
            <div className="flex flex-wrap justify-between space-x-3 gap-2">
              {!isCustomJob() && ((!isSharedView && !isInProgress && !isCompleted && !isInspectionJob && !isReadyForInspection) || 
                (isSharedView && !isInProgress && !isCompleted && !isReadyForInspection)) ? (
                <button
                  onClick={handleStartClean}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  Start Job
                </button>
              ) : null}
              {((!isSharedView && isInProgress && !isCompleted && !isInspectionJob && !isReadyForInspection) || 
                (isSharedView && isInProgress && !isCompleted && !isReadyForInspection) ||
                (isCustomJob() && !isCompleted && !isReadyForInspection)) ? (
                <button
                  onClick={handleCompleteClean}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
                >
                  Complete Job
                </button>
              ) : null}
              {(isCompleted || isReadyForInspection) && (
                <div className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${getStatusColorClass()} cursor-default whitespace-nowrap`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {getStatusText()}
                </div>
              )}
              {job?.serviceType?.checklist && !job?.serviceType?.useChecklistStyle && (
                !isSharedView ? (
                  <button
                    onClick={handleOpenChecklistModal}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 whitespace-nowrap"
                  >
                    Complete Checklist
                  </button>
                ) : (
                  <button
                    onClick={handleOpenChecklistModal}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 whitespace-nowrap"
                  >
                    View Checklist
                  </button>
                )
              )}
              <button
                onClick={handleShare}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 whitespace-nowrap"
              >
                <svg className="h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                </svg>
                Share
              </button>
            </div>
            {isInspectionComplete && (
              <div className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-100 text-purple-800">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pre-arrival Inspection has already been completed
              </div>
            )}
          </div>
        </div>
      </div>

      {isInspectionJob && (
        <div className="mx-2 lg:mx-4 mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-blue-800 text-sm font-medium">
              For Inspection Jobs, please complete checklists to mark job as complete
            </p>
          </div>
        </div>
      )}

      {/* Desktop header */}
      <div className="hidden lg:block max-w-7xl mx-auto px-3 lg:px-4 mt-3">
        <div className="bg-white shadow shadow rounded-lg">
          <div className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {!isSharedView && (
                  <button
                    onClick={() => navigate('/dashboard/schedule')}
                    className="mr-4 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 whitespace-nowrap"
                  >
                    View all Jobs
                  </button>
                )}
                <h1 className="text-2xl font-bold text-gray-900">{job.property.name}</h1>
              </div>
              <div className="flex items-center space-x-4">
                {!isCustomJob() && ((!isSharedView && !isInProgress && !isCompleted && !isInspectionJob && !isReadyForInspection) || 
                  (isSharedView && !isInProgress && !isCompleted && !isReadyForInspection)) ? (
                  <button
                    onClick={handleStartClean}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    Start Job
                  </button>
                ) : null}
                {((!isSharedView && isInProgress && !isCompleted && !isInspectionJob && !isReadyForInspection) || 
                  (isSharedView && isInProgress && !isCompleted && !isReadyForInspection) ||
                  (isCustomJob() && !isCompleted && !isReadyForInspection)) ? (
                  <button
                    onClick={handleCompleteClean}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
                  >
                    Complete Job
                  </button>
                ) : null}
                {(isCompleted || isReadyForInspection) && (
                  <div className={`flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${getStatusColorClass()} cursor-default whitespace-nowrap`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {getStatusText()}
                  </div>
                )}
                {job?.serviceType?.checklist && !job?.serviceType?.useChecklistStyle && (
                  !isSharedView ? (
                    <button
                      onClick={handleOpenChecklistModal}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-sky-600 hover:bg-sky-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 whitespace-nowrap"
                    >
                      Complete Checklist
                    </button>
                  ) : (
                    <button
                      onClick={handleOpenChecklistModal}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 whitespace-nowrap"
                    >
                      View Checklist
                    </button>
                  )
                )}
                <button
                  onClick={handleShare}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 whitespace-nowrap"
                >
                  <svg className="h-5 w-5 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                  </svg>
                  Share
                </button>
              </div>
            </div>
            {startTimeEntry && !completionEntry && (
              <div className="mt-2 inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800">
                <svg className="h-5 w-5 mr-1.5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Job Start: {formatDateTime(startTimeEntry.timestamp)}
                {startTimeEntry.updatedBy && <span> by {startTimeEntry.updatedBy.name}</span>}
              </div>
            )}
            {completionEntry && (
              <div className="space-y-2 mt-2">
                {startTimeEntry && (
                  <div className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800">
                    <svg className="h-5 w-5 mr-1.5 text-blue-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    Job Start: {formatDateTime(startTimeEntry.timestamp)}
                  </div>
                )}
                <div className="inline-flex items-center ml-2 px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-800">
                  <svg className="h-5 w-5 mr-1.5 text-green-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  {completionStatusEntry?.status === 'READY_FOR_INSPECTION'
                    ? `Ready for inspection at ${formatDateTime(completionStatusEntry.timestamp)}`
                    : `Job completed at ${formatDateTime(completionEntry.timestamp)}`}
                </div>
              </div>
            )}
            {isInspectionComplete && (
              <div className="mt-1 inline-flex items-center px-2 py-1 rounded-md text-xs bg-purple-100 text-purple-800">
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pre-arrival Inspection has already been completed
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default JobHeader;
